import { useEffect, useRef, useCallback, useState } from "react";
import { getStoredAuth } from "../auth";

export interface SSEEvent {
  event: string;
  data: unknown;
  timestamp: number;
}

export interface UseSSEOptions {
  enabled?: boolean;
  authToken?: string | null;
}

export function shouldConnectSse({
  url,
  enabled = true,
  authToken,
}: {
  url: string;
  enabled?: boolean;
  authToken?: string | null;
}): boolean {
  if (!enabled) return false;
  if (url.startsWith("/api/events")) return Boolean(authToken);
  return true;
}

export function buildSseUrl({
  url,
  authToken,
}: {
  url: string;
  authToken?: string | null;
}): string {
  return url.startsWith("/api/events") && authToken
    ? `${url}${url.includes("?") ? "&" : "?"}token=${encodeURIComponent(authToken)}`
    : url;
}

/**
 * React hook for Server-Sent Events (SSE) connection.
 * Automatically reconnects on disconnect with exponential backoff.
 */
export function useSSE(url: string, options: UseSSEOptions = {}) {
  const [connected, setConnected] = useState(false);
  const [events, setEvents] = useState<SSEEvent[]>([]);
  const sourceRef = useRef<EventSource | null>(null);
  const reconnectTimer = useRef<number | null>(null);
  const reconnectDelay = useRef(1000);
  const enabled = options.enabled ?? true;

  const connect = useCallback(() => {
    if (sourceRef.current) {
      sourceRef.current.close();
      sourceRef.current = null;
    }

    const authToken = options.authToken ?? getStoredAuth().token;
    if (!shouldConnectSse({ url, enabled, authToken })) {
      setConnected(false);
      return;
    }

    const eventUrl = buildSseUrl({ url, authToken });
    const source = new EventSource(eventUrl);
    sourceRef.current = source;

    source.addEventListener("connected", () => {
      setConnected(true);
      reconnectDelay.current = 1000; // Reset backoff on success
    });

    source.addEventListener("audit:new", (e) => {
      try {
        const data = JSON.parse(e.data);
        setEvents((prev) => [
          { event: "audit:new", data, timestamp: Date.now() },
          ...prev.slice(0, 99),
        ]);
      } catch {
        /* ignore */
      }
    });

    source.addEventListener("health:change", (e) => {
      try {
        const data = JSON.parse(e.data);
        setEvents((prev) => [
          { event: "health:change", data, timestamp: Date.now() },
          ...prev.slice(0, 99),
        ]);
      } catch {
        /* ignore */
      }
    });

    source.addEventListener("alert", (e) => {
      try {
        const data = JSON.parse(e.data);
        setEvents((prev) => [
          { event: "alert", data, timestamp: Date.now() },
          ...prev.slice(0, 99),
        ]);
      } catch {
        /* ignore */
      }
    });

    // Canvas SSE events
    for (const eventName of [
      "canvas:artifact_created",
      "canvas:artifact_updated",
      "canvas:block_updated",
      "canvas:question_answered",
    ]) {
      source.addEventListener(eventName, (e) => {
        try {
          const data = JSON.parse((e as MessageEvent).data);
          setEvents((prev) => [
            { event: eventName, data, timestamp: Date.now() },
            ...prev.slice(0, 99),
          ]);
        } catch {
          /* ignore */
        }
      });
    }

    // Approval SSE events
    for (const eventName of ["approval:new", "approval:updated"]) {
      source.addEventListener(eventName, (e) => {
        try {
          const data = JSON.parse((e as MessageEvent).data);
          setEvents((prev) => [
            { event: eventName, data, timestamp: Date.now() },
            ...prev.slice(0, 99),
          ]);
        } catch {
          /* ignore */
        }
      });
    }

    // Security-specific SSE events
    for (const eventName of [
      "security:alert",
      "security:feedback",
      "security:exception",
      "security:recommendation",
      "security:recommendation:new",
    ]) {
      source.addEventListener(eventName, (e) => {
        try {
          const data = JSON.parse((e as MessageEvent).data);
          setEvents((prev) => [
            { event: eventName, data, timestamp: Date.now() },
            ...prev.slice(0, 99),
          ]);
        } catch {
          /* ignore */
        }
      });
    }

    source.onerror = () => {
      setConnected(false);
      source.close();
      sourceRef.current = null;

      // Reconnect with exponential backoff (max 30s)
      const delay = Math.min(reconnectDelay.current, 30_000);
      reconnectTimer.current = window.setTimeout(() => {
        reconnectDelay.current *= 2;
        connect();
      }, delay);
    };
  }, [enabled, options.authToken, url]);

  useEffect(() => {
    connect();

    return () => {
      if (sourceRef.current) {
        sourceRef.current.close();
      }
      if (reconnectTimer.current) {
        clearTimeout(reconnectTimer.current);
      }
    };
  }, [connect]);

  const clearEvents = useCallback(() => {
    setEvents([]);
  }, []);

  return { connected, events, clearEvents };
}
